<template>
  <div>
    <div class="list-toolbar">
      <div class="toolbar-top">
        <div>
          <h2 class="list-title">Team Timesheets</h2>
          <p class="list-subtitle">
            Approved timesheets across every project you supervise
          </p>
        </div>
        <div class="toolbar-actions">
          <b-button variant="secondary" size="sm" @click="reload">
            <b-icon-arrow-clockwise /> Refresh
          </b-button>
        </div>
      </div>

      <div class="toolbar-row">
        <div class="filter-field filter-field-date">
          <label class="filter-label">From</label>
          <b-form-datepicker
            v-model="dateFrom"
            locale="en-AU"
            :date-format-options="dateFormatOptions"
            size="sm"
            today-button
            reset-button
            close-button
            show-decade-nav
            class="date-input"
            @input="onDateFromChange"
          />
        </div>

        <div class="filter-field filter-field-date">
          <label class="filter-label">To</label>
          <b-form-datepicker
            v-model="dateTo"
            locale="en-AU"
            :date-format-options="dateFormatOptions"
            size="sm"
            today-button
            reset-button
            close-button
            show-decade-nav
            class="date-input"
            @input="reload"
          />
        </div>

        <div class="filter-field">
          <label class="filter-label">Project</label>
          <b-form-select
            v-model="selectedProjectId"
            :options="projectOptions"
            size="sm"
            class="filter-control filter-control-project"
            @change="reload"
          />
        </div>

        <div class="filter-field">
          <label class="filter-label">Week</label>
          <div class="week-nav">
            <b-button
              variant="secondary"
              size="sm"
              class="week-nav-btn"
              aria-label="Current week"
              title="Current week"
              @click="applyCurrentWeek"
            >
              <b-icon-calendar3 />
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="week-nav-btn"
              aria-label="Previous week"
              @click="shiftWeek(-1)"
            >
              <b-icon-chevron-left />
            </b-button>
            <span class="week-nav-label">{{ weekLabel }}</span>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="week-nav-btn"
              aria-label="Next week"
              @click="shiftWeek(1)"
            >
              <b-icon-chevron-right />
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
    </div>

    <!-- Empty state -->
    <div v-else-if="sheets.length === 0" class="text-center py-5 text-muted">
      <p>No approved team timesheets for this range.</p>
    </div>

    <!-- Timesheet cards -->
    <div v-else class="team-card-list">
      <div
        v-for="ts in sheets"
        :key="ts.id"
        class="timesheet-card"
        :data-timesheet-id="ts.id"
      >
        <!-- Card header — always visible -->
        <div
          class="timesheet-card-header cursor-pointer"
          @click="toggleExpand(ts.id)"
        >
          <div class="timesheet-card-title-group">
            <span class="timesheet-project">{{ ts.project_name }}</span>
            <span class="timesheet-meta">{{ formatWeek(ts.week_start) }}</span>
            <span class="timesheet-meta"
              >Submitted by {{ submittedBy(ts) }}</span
            >
            <span class="timesheet-meta"
              >{{ onSiteCount(ts) }} worker{{
                onSiteCount(ts) !== 1 ? 's' : ''
              }}
              on site</span
            >
          </div>

          <div class="timesheet-card-status">
            <b-badge pill variant="success" class="px-3 py-1 status-pill">
              Approved
            </b-badge>
            <b-icon :icon="expanded[ts.id] ? 'chevron-up' : 'chevron-down'" />
          </div>
        </div>

        <!-- Expanded detail. `lazy` matters here: without it every card's
             body — including full-resolution submission photos — mounts
             into the DOM (just display:none) for every sheet on the page,
             not only the expanded one, so the browser fetches every photo
             on every sheet up front. `lazy` defers that render until a
             card is first opened. Same fix as SupervisorTimesheetTab.vue. -->
        <b-collapse :visible="!!expanded[ts.id]" lazy>
          <div class="card-body-soft">
            <!-- Restricted state — a peer supervisor's sheet. The API
                 already strips hours/comments/photos/audit for a non-own
                 sheet (SupervisorTimesheetController::_toTeamSummary); this
                 notice makes that explicit instead of rendering the
                 stripped fields as if they were real empty data. -->
            <div
              v-if="!ts.is_own_sheet"
              class="context-item restricted-notice mb-3"
            >
              <b-icon-lock-fill class="context-icon" />
              <strong>Hours not shown</strong>
              <span
                >This is {{ submittedBy(ts) }}'s timesheet. You can see it
                exists so you don't double-enter a shared worker's hours, but
                only they and a business admin can see the detail.</span
              >
            </div>

            <!-- Compact submission context -->
            <div
              v-if="ts.is_own_sheet && ts.supervisor_comment"
              class="context-item mb-3"
            >
              <b-icon-chat-left-text class="context-icon" />
              <strong>Supervisor note</strong>
              <span>{{ ts.supervisor_comment }}</span>
            </div>

            <div
              v-if="ts.is_own_sheet && ts.photo_urls && ts.photo_urls.length"
              class="context-item context-item-photos mb-3"
            >
              <b-icon-camera class="context-icon" />
              <strong>Photos</strong>
              <div class="event-photos">
                <button
                  v-for="(photo, i) in ts.photo_urls"
                  :key="photo.id"
                  type="button"
                  class="photo-thumb-button"
                  @click="openLightbox(ts.photo_urls, i)"
                >
                  <img
                    :src="photo.url"
                    class="event-photo-thumb"
                    :alt="'Photo ' + (i + 1)"
                  />
                </button>
              </div>
            </div>

            <!-- Worker hours table (read-only, no leave/edit affordances) —
                 own sheet only; a peer's sheet shows the restricted notice
                 above instead. -->
            <div
              v-if="ts.is_own_sheet"
              class="table-responsive mb-3 worker-table-wrap"
            >
              <table class="table table-sm mb-0 worker-table">
                <thead>
                  <tr>
                    <th class="worker-heading">Worker</th>
                    <th
                      v-for="day in weekDays(ts.week_start)"
                      :key="day.label"
                      class="day-heading text-center small"
                    >
                      <span class="day-heading-label">{{ day.label }}</span>
                      <span class="day-heading-date">{{ day.date }}</span>
                    </th>
                    <th class="text-center total-heading">Total</th>
                    <th class="text-center on-site-heading">On Site</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in tokenRows(ts)" :key="row.worker.id">
                    <td class="align-middle worker-name-cell">
                      <div class="worker-identity">
                        <div class="worker-name">
                          {{ row.worker.full_name }}
                        </div>
                        <div v-if="row.worker.trade" class="worker-trade">
                          {{ formatTrade(row.worker.trade) }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(day, i) in row.days"
                      :key="i"
                      class="readonly-day-cell text-center align-middle small"
                      :class="{ 'readonly-day-cell-empty': !day.present }"
                    >
                      <template v-if="day.present && day.start && day.finish">
                        <div class="readonly-day-card">
                          <span class="readonly-day-time">
                            {{ day.start }} – {{ day.finish }}
                          </span>
                          <span class="readonly-day-summary">
                            <span v-if="dayHoursLabel(day)">{{
                              dayHoursLabel(day)
                            }}</span>
                            <span
                              v-if="dayHoursLabel(day) && day.break_minutes"
                              aria-hidden="true"
                            >
                              ·
                            </span>
                            <span v-if="day.break_minutes">
                              {{ day.break_minutes }}m break
                            </span>
                          </span>
                        </div>
                      </template>
                      <span v-else class="empty-day-pill">Not added</span>
                    </td>
                    <td
                      class="text-center align-middle font-weight-bold small total-cell"
                    >
                      <span class="total-hours-badge">{{ rowTotal(row) }}</span>
                    </td>
                    <td class="text-center align-middle on-site-cell">
                      <b-badge
                        pill
                        :variant="row.on_site ? 'success' : 'secondary'"
                        class="on-site-badge"
                      >
                        {{ row.on_site ? 'On Site' : 'Off Site' }}
                      </b-badge>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Invited tradies without a token yet — no hours possible until eligible -->
            <supervisor-pending-workers-panel
              v-if="ts.is_own_sheet"
              :workers="ts.pending_workers || []"
              class="mb-3"
            />

            <!-- Audit trail -->
            <supervisor-audit-trail
              v-if="ts.is_own_sheet && ts.audit && ts.audit.length"
              :audit="ts.audit"
            />
          </div>
        </b-collapse>
      </div>
    </div>

    <!-- Photo lightbox -->
    <b-modal
      id="team-timesheet-photo-lightbox-modal"
      size="xl"
      centered
      hide-header
      :hide-footer="lightboxPhotos.length <= 1"
      body-class="p-1 text-center bg-dark"
      footer-class="justify-content-center"
      @hidden="
        lightboxPhotos = []
        lightboxIndex = 0
      "
    >
      <img
        v-if="lightboxPhotos.length"
        :src="lightboxPhotos[lightboxIndex]"
        style="max-width: 100%; max-height: 80vh; object-fit: contain"
      />
      <template #modal-footer>
        <b-button variant="light" @click="lightboxPrev">&#8592; Prev</b-button>
        <span class="mx-3 text-white"
          >{{ lightboxIndex + 1 }} / {{ lightboxPhotos.length }}</span
        >
        <b-button variant="light" @click="lightboxNext">Next &#8594;</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Api from '@/api'
import {
  formatTrade,
  dayMinutes,
  formatHours,
  tokenRows as sharedTokenRows,
} from '@/utils/timesheetHelpers'
import { startDayOf, weekDates, formatDayHeader } from '@/utils/weekHelpers'
import SupervisorPendingWorkersPanel from '@/components/timesheets/supervisor/SupervisorPendingWorkersPanel.vue'
import SupervisorAuditTrail from '@/components/timesheets/supervisor/SupervisorAuditTrail.vue'

export default {
  name: 'TeamTimesheetTab',

  components: {
    SupervisorPendingWorkersPanel,
    SupervisorAuditTrail,
  },

  data() {
    return {
      loading: false,
      sheets: [],
      expanded: {},
      dateFrom: '',
      dateTo: '',
      // en-AU default renders "29 August 2026"; force numeric dd/mm/yyyy.
      dateFormatOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
      selectedProjectId: null,
      lightboxPhotos: [],
      lightboxIndex: 0,
    }
  },

  computed: {
    projectOptions() {
      return this.$store.getters['timesheet/projectOptions']
    },

    weekLabel() {
      if (!this.dateFrom || !this.dateTo) return '-'
      const from = new Date(this.dateFrom + 'T00:00:00')
      const to = new Date(this.dateTo + 'T00:00:00')
      const month = (d) => d.toLocaleDateString('en-AU', { month: 'short' })
      // Non-breaking space inside each date so the label can only ever wrap
      // around the dash, never mid-date.
      const nbsp = ' '
      if (
        from.getMonth() === to.getMonth() &&
        from.getFullYear() === to.getFullYear()
      ) {
        // Same month: compact "13–19 Jul" fits phone-width filter columns.
        return `${from.getDate()}–${to.getDate()}${nbsp}${month(to)}`
      }
      return `${from.getDate()}${nbsp}${month(
        from,
      )} – ${to.getDate()}${nbsp}${month(to)}`
    },
  },

  async mounted() {
    if (!this.$store.state.project.projects.length) {
      this.$store
        .dispatch('project/getProjects', { limit: 100, is_archived: false })
        .catch(function () {})
    }
    // Cached after first load — cheap. Must resolve before applyCurrentWeek()
    // so the default week honours the business's configured start day.
    await this.$store.dispatch('timesheet/getTimesheetSettings')
    // Per-project pay-period overrides — lets applyCurrentWeek() honour a
    // project that runs a different week. Cheap + cached; failure is
    // swallowed and the company schedule is used. Must also resolve before
    // applyCurrentWeek() — otherwise the first computed range silently uses
    // the company default even for a project with its own override, since
    // the override list hasn't loaded yet.
    await this.$store.dispatch('timesheet/getProjectPayPeriods')
    // applyCurrentWeek() sets the default From/To range and triggers the
    // initial load() via reload(); no separate load() call is needed.
    this.applyCurrentWeek()
  },

  methods: {
    formatTrade,

    tokenRows(ts) {
      return sharedTokenRows(ts)
    },

    // Scoped to the same rows tokenRows() renders in the table below, so
    // this count can never exceed what the supervisor actually sees — a
    // worker without has_token doesn't get a table row (matches
    // SupervisorTimesheetTab's convention), so it must not be counted here
    // either.
    onSiteCount(ts) {
      return this.tokenRows(ts).filter((r) => r.on_site).length
    },

    submittedBy(ts) {
      return ts.submitted_by_name || 'Unknown'
    },

    dayHoursLabel(day) {
      return formatHours(dayMinutes(day)) || null
    },

    rowTotal(row) {
      const mins = row.days.reduce((sum, day) => sum + dayMinutes(day), 0)
      return mins > 0 ? formatHours(mins) : '-'
    },

    weekDays(weekStart) {
      return weekDates(weekStart).map((iso) => {
        const { weekday, dayLabel } = formatDayHeader(iso)
        return { label: weekday, date: dayLabel }
      })
    },

    formatWeek(weekStart) {
      const base = new Date(weekStart + 'T00:00:00')
      const end = new Date(base)
      end.setDate(base.getDate() + 6)
      const fmt = (d) =>
        d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
      return `${fmt(base)} – ${fmt(end)}`
    },

    toggleExpand(id) {
      this.$set(this.expanded, id, !this.expanded[id])
    },

    applyCurrentWeek() {
      const startDay = this.$store.getters['timesheet/projectStartDay'](
        this.selectedProjectId,
      )
      const weekStart = startDayOf(null, startDay)
      this.dateFrom = weekStart
      this.dateTo = weekDates(weekStart)[6]
      this.reload()
    },

    onDateFromChange() {
      if (!this.dateFrom) return
      const from = new Date(this.dateFrom + 'T00:00:00')
      const to = new Date(from)
      to.setDate(from.getDate() + 6)
      this.dateTo = to.toISOString().slice(0, 10)
      this.reload()
    },

    shiftWeek(direction) {
      const from = new Date((this.dateFrom || this.dateTo) + 'T00:00:00')
      const to = new Date((this.dateTo || this.dateFrom) + 'T00:00:00')
      from.setDate(from.getDate() + direction * 7)
      to.setDate(to.getDate() + direction * 7)
      const fmt = (d) => d.toISOString().slice(0, 10)
      this.dateFrom = fmt(from)
      this.dateTo = fmt(to)
      this.reload()
    },

    reload() {
      this.load()
    },

    async load() {
      this.loading = true
      try {
        var params = {}
        if (this.dateFrom) params.week_start_from = this.dateFrom
        if (this.dateTo) params.week_start_to = this.dateTo
        if (this.selectedProjectId) params.project_id = this.selectedProjectId
        var res = await Api.get('/supervisor/team-timesheets', { params })
        this.sheets = res.data.data || []
      } catch (e) {
        this.$bvToast.toast('Failed to load team timesheets.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },

    openLightbox(photos, index) {
      this.lightboxPhotos = photos.map((p) => p.url)
      this.lightboxIndex = index
      this.$bvModal.show('team-timesheet-photo-lightbox-modal')
    },
    lightboxPrev() {
      this.lightboxIndex =
        (this.lightboxIndex - 1 + this.lightboxPhotos.length) %
        this.lightboxPhotos.length
    },
    lightboxNext() {
      this.lightboxIndex = (this.lightboxIndex + 1) % this.lightboxPhotos.length
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';

.list-toolbar {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 20px;
  margin-bottom: 1rem;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);
}

.toolbar-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.list-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.01em;
}

.list-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.toolbar-row {
  display: grid;
  grid-template-columns:
    minmax(132px, 0.75fr) minmax(132px, 0.75fr) minmax(180px, 1.1fr)
    minmax(180px, 1fr);
  align-items: flex-end;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(150px, 1fr));
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.filter-field-date {
  min-width: 132px;
}

.date-input,
.filter-control {
  width: 100%;
}

/* b-form-datepicker's two-class .b-form-btn-label-control.form-control rule
   beats the app-wide one-class `.form-control` height, so it renders shorter
   than the sibling sm filter controls. Match it back. */
/deep/ .date-input.form-control-sm {
  height: 34px;
  display: flex;
  align-items: center;
}

.filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
}

.week-nav {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
}

.week-nav-btn {
  width: 32px;
  padding-left: 0;
  padding-right: 0;
  flex-shrink: 0;
}

.week-nav-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  flex: 1 1 0;
  min-width: 0;
  text-align: center;
  line-height: 1.2;
  white-space: normal;
  overflow: visible;
}

.team-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.timesheet-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}

.timesheet-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  flex-wrap: wrap;
}

.cursor-pointer {
  cursor: pointer;
}

.timesheet-card-title-group {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.timesheet-project {
  font-weight: 700;
  color: #111827;
  font-size: 14px;
}

.timesheet-meta {
  font-size: 12px;
  color: #6b7280;
}

.timesheet-card-status {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-body-soft {
  padding: 16px 18px 18px;
  border-top: 1px solid #f3f4f6;
  background: #fbfcfe;
}

.context-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;

  strong {
    font-weight: 600;
  }
}

.context-item-photos {
  align-items: flex-start;
}

.context-icon {
  color: #9ca3af;
  flex-shrink: 0;
}

.event-photos {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.photo-thumb-button {
  padding: 0;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: none;
  overflow: hidden;
  cursor: pointer;
}

.event-photo-thumb {
  width: 56px;
  height: 56px;
  object-fit: cover;
  display: block;
}

.worker-table-wrap {
  border: 1px solid #f3f4f6;
  border-radius: 10px;
  overflow: hidden;
}

/deep/ .worker-table {
  @include timesheet-table-headers;
}

.worker-identity {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.worker-name {
  font-weight: 600;
  color: #111827;
  font-size: 13px;
}

.worker-trade {
  font-size: 11px;
  color: #9ca3af;
}

.readonly-day-card {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.readonly-day-time {
  font-size: 12px;
  font-weight: 600;
  color: #111827;
}

.readonly-day-summary {
  font-size: 11px;
  color: #6b7280;
}

.readonly-day-cell-empty {
  color: #d1d5db;
}

.empty-day-pill {
  font-size: 11px;
  color: #d1d5db;
}

.total-hours-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 12px;
}

.on-site-badge {
  font-size: 11px;
}
</style>
